import React, {useEffect, useRef} from "react";
import {Animated, StyleSheet, View, Easing, Platform, AccessibilityInfo} from "react-native";
import {PALETTE} from "./lib/palette";

/**
 * Session 만료 중 **Leave로 나갈 때만** 보여주는 작별 화면.
 * 시퀀스: 매장 로고가 한 번 숨쉬고(약 3초) → 정지 → 손(👋)이 나타나 계속 흔든다.
 * "See you next time!" 페이드업 + accent 밑줄 드로우. prefers-reduced-motion이면 정지 최종상태.
 * (Leave가 URL에 shopId를 남겨 로고 effect가 실제 매장 로고를 로드 → logoSource로 전달.)
 */
export default function Farewell({logoSource, accent}: {logoSource: any; accent: string}) {
    const breathe = useRef(new Animated.Value(0)).current;   // 0→1→0 한 번
    const bye = useRef(new Animated.Value(0)).current;
    const ul = useRef(new Animated.Value(0)).current;
    const hint = useRef(new Animated.Value(0)).current;
    const hand = useRef(new Animated.Value(0)).current;      // 손 opacity
    const wave = useRef(new Animated.Value(0)).current;      // 흔들기 rotation 드라이버

    useEffect(() => {
        let cancelled = false;
        let loop: Animated.CompositeAnimation | null = null;

        const run = (reduce: boolean) => {
            if (reduce) { bye.setValue(1); ul.setValue(1); hint.setValue(1); hand.setValue(1); return; }

            // 텍스트/밑줄 등장(약간의 지연)
            Animated.sequence([Animated.delay(250), Animated.timing(bye, {toValue: 1, duration: 500, useNativeDriver: false})]).start();
            Animated.sequence([Animated.delay(650), Animated.timing(ul, {toValue: 1, duration: 500, easing: Easing.out(Easing.cubic), useNativeDriver: false})]).start();
            Animated.sequence([Animated.delay(950), Animated.timing(hint, {toValue: 1, duration: 500, useNativeDriver: false})]).start();

            // 로고 숨 한 번(약 3초)
            Animated.sequence([
                Animated.timing(breathe, {toValue: 1, duration: 1500, easing: Easing.inOut(Easing.quad), useNativeDriver: false}),
                Animated.timing(breathe, {toValue: 0, duration: 1500, easing: Easing.inOut(Easing.quad), useNativeDriver: false}),
            ]).start();

            // 3초 후: 손 페이드인 + 흔들기 루프
            Animated.sequence([Animated.delay(3000), Animated.timing(hand, {toValue: 1, duration: 350, useNativeDriver: false})]).start(() => {
                if (cancelled) return;
                const seg = (to: number) => Animated.timing(wave, {toValue: to, duration: 150, easing: Easing.inOut(Easing.quad), useNativeDriver: false});
                loop = Animated.loop(Animated.sequence([
                    seg(1), seg(-1), seg(1), seg(-0.5), seg(0.7), seg(0), Animated.delay(1500),
                ]));
                loop.start();
            });
        };

        if (Platform.OS === "web") {
            const reduce = typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
            run(reduce);
        } else {
            AccessibilityInfo.isReduceMotionEnabled().then((r) => { if (!cancelled) run(!!r); }).catch(() => { if (!cancelled) run(false); });
        }

        return () => { cancelled = true; if (loop) loop.stop(); };
    }, []);

    const logoStyle = {
        transform: [
            {translateY: breathe.interpolate({inputRange: [0, 1], outputRange: [0, -7]})},
            {scale: breathe.interpolate({inputRange: [0, 1], outputRange: [1, 1.03]})},
        ],
    };
    const handStyle = {opacity: hand, transform: [{rotate: wave.interpolate({inputRange: [-1, 1], outputRange: ["-12deg", "16deg"]})}]};
    const byeStyle = {opacity: bye, transform: [{translateY: bye.interpolate({inputRange: [0, 1], outputRange: [12, 0]})}]};
    const ulStyle = {transform: [{scaleX: ul}]};
    const hintStyle = {opacity: hint, transform: [{translateY: hint.interpolate({inputRange: [0, 1], outputRange: [12, 0]})}]};

    return (
        <View style={styles.wrap}>
            <Animated.Image style={[styles.logo, logoStyle]} source={logoSource} resizeMode="contain" accessibilityLabel="logo" />
            <Animated.Text style={[styles.hand, handStyle]}>👋</Animated.Text>
            <Animated.Text style={[styles.bye, byeStyle]}>See you next time!</Animated.Text>
            <Animated.View style={[styles.ul, {backgroundColor: accent}, ulStyle]} />
            <Animated.Text style={[styles.hint, hintStyle]}>Scan the QR code to order again.</Animated.Text>
        </View>
    );
}

const styles = StyleSheet.create({
    wrap: {
        flex: 1,
        alignItems: "center",
        // 로고를 Splash와 동일하게 상단 정렬(중앙정렬 금지). flow의 paddingTop이 위치를 맞춘다.
    },
    logo: {
        width: 200,
        height: 200,   // 처음 Splash 화면과 동일 크기
    },
    hand: {
        fontSize: 36,
        lineHeight: 44,
        marginTop: 40,   // 로고 아래 여백(손과 떨어지게)
    },
    bye: {
        fontSize: 23,
        fontWeight: "800",
        letterSpacing: -0.2,
        color: PALETTE.TEXT,
        marginTop: 10,
        textAlign: "center",
    },
    ul: {
        height: 3,
        width: 72,
        borderRadius: 2,
        marginTop: 12,
    },
    hint: {
        fontSize: 13,
        color: PALETTE.MUTED,
        lineHeight: 20,
        marginTop: 12,
        textAlign: "center",
        maxWidth: 220,
    },
});
